/// <reference path="../../../../node_modules/@ms/office-online-strings/en-US/transcription-strings.d.ts" />
import * as React from 'react';
import { AppTranscriptCardProps, ITranscriptData } from './AppTranscriptCard.props';
import { appTranscriptCardIconButtonClassName, getAppTranscriptCardStyles, getAppTranscriptCardCheckboxStyles, getAppTranscriptCardIconButtonStyles, getAppTranscriptCardSpeakerLabelStyles, getAppTranscriptCardTimestampLinkStyles, getAppTranscriptCardTextFieldStyles, getAppTranscriptCardTimestampLabelStyles, getAppTranscriptCardTranscriptLabelStyles, getAddToDocumentTooltipHostStyles, getAppTranscriptCardScrollStyles } from './AppTranscriptCard.styles';
import { TextField, Checkbox, ICheckbox, Link, Stack, Label, DefaultButton, IIconProps, ITextField, IButton } from 'office-ui-fabric-react';
import { FormatTimeDescription, FormatTime } from '../../utils';
import { AddTooltipToElement } from '../Tooltip/AddTooltipToElement';

interface AppTranscriptCardState {
  isPendingEditButtonFocus: boolean;
}

const addIcon: IIconProps = { iconName: 'CircleAddition' };
const cancelIcon: IIconProps = { iconName: 'Cancel' };
const confirmIcon: IIconProps = { iconName: 'CheckMark' };
const editIcon: IIconProps = { iconName: 'Edit' };

export class AppTranscriptCard extends React.Component<AppTranscriptCardProps, AppTranscriptCardState> {
  private rootDivRef = React.createRef<HTMLDivElement>();
  private speakerTextField = React.createRef<ITextField>();
  private transcriptionTextField = React.createRef<ITextField>();
  private editButtonRef = React.createRef<IButton>();
  private changeAllCheckboxRef = React.createRef<ICheckbox>();

  constructor(props: AppTranscriptCardProps) {
    super(props);

    this.state = {
      isPendingEditButtonFocus: false
    };
  }

  public componentDidUpdate(prevProps: AppTranscriptCardProps) {
    if (this.props.isEditing && !prevProps.isEditing)
      this.transcriptionTextField.current.focus();

    if (this.state.isPendingEditButtonFocus) {
      this.editButtonRef.current.focus();

      this.setState({
        isPendingEditButtonFocus: false
      });
    }
  }

  public render() {
    const { transcriptionData, styles, isHighlighted, isEditing, shouldScrollToHighlightedIndex } = this.props;
    const appTranscriptCardStyles = getAppTranscriptCardStyles(isHighlighted, styles);

    if (isHighlighted && shouldScrollToHighlightedIndex) {
      this.rootDivRef.current && this.rootDivRef.current.scrollIntoView(getAppTranscriptCardScrollStyles());
    }

    if (!transcriptionData) {
      return null;
    }

    // If we are pending setting focus to the edit button, make it visible first.
    // Otherwise we won't be able to set focus to it.
    const forceEditButtonVisibility: boolean = this.state.isPendingEditButtonFocus;

    return (
      <div ref={this.rootDivRef} className={appTranscriptCardStyles.root}>
        {
        isEditing ?
          (<Stack>
            <div className={appTranscriptCardStyles.topRowContainer}>
              {this._getTimestampElement(transcriptionData)}
              <TextField componentRef={this.speakerTextField} styles={getAppTranscriptCardTextFieldStyles('speaker')} defaultValue={transcriptionData.speaker} />
            </div>
            <div>
              <Checkbox componentRef={this.changeAllCheckboxRef} styles={getAppTranscriptCardCheckboxStyles()} label={`${TranscriptionStrings.TranscriptEditingChangeAllLabel} ${transcriptionData.speaker}`} />
            </div>
            <div>
              <TextField name={TranscriptionStrings.EditTranscriptSectionFieldName} componentRef={this.transcriptionTextField} styles={getAppTranscriptCardTextFieldStyles('transcript')} defaultValue={transcriptionData.text} autoAdjustHeight={true} multiline={true} resizable={false} />
            </div>
            <div className={appTranscriptCardStyles.bottomRowContainer}>
            {AddTooltipToElement(<DefaultButton aria-label={TranscriptionStrings.CancelButtonName} iconProps={cancelIcon} styles={getAppTranscriptCardIconButtonStyles('cancel')} onClick={(event)=>this._onCancelClicked(event)} />, TranscriptionStrings.CancelButtonName)}
            {AddTooltipToElement(<DefaultButton aria-label={TranscriptionStrings.ConfirmButtonName} iconProps={confirmIcon} styles={getAppTranscriptCardIconButtonStyles('confirm')} onClick={this._onConfirmClicked} />, TranscriptionStrings.ConfirmButtonName)}
            </div>
          </Stack>) :
          (<Stack>
            <div className={appTranscriptCardStyles.topRowContainer}>
              {this._getTimestampElement(transcriptionData)}
              <Label styles={getAppTranscriptCardSpeakerLabelStyles()}>{transcriptionData.speaker}</Label>
              {AddTooltipToElement(<DefaultButton componentRef={this.editButtonRef} aria-label={TranscriptionStrings.EditTranscriptSectionButtonName} className={appTranscriptCardIconButtonClassName} iconProps={editIcon} onClick={this._onEditClicked} styles={getAppTranscriptCardIconButtonStyles('edit', forceEditButtonVisibility)} />, TranscriptionStrings.EditTranscriptSectionButtonName)}
              {AddTooltipToElement(<DefaultButton aria-label={TranscriptionStrings.AddSectionToDocumentButtonName} className={appTranscriptCardIconButtonClassName} iconProps={addIcon} styles={getAppTranscriptCardIconButtonStyles('add')} onClick={(event)=>this._onAddClicked(event)} />, TranscriptionStrings.AddSectionToDocumentButtonName, getAddToDocumentTooltipHostStyles())}
            </div>
            <Label styles={getAppTranscriptCardTranscriptLabelStyles()}>{transcriptionData.text}</Label>
          </Stack>)
        }
      </div>
    );
  }

  private _getTimestampElement(transcriptionData: ITranscriptData) {
    // If we have an onTimestampClicked callback, show timestamps as links
    if (this.props.onTimestampClicked != null)
      return <Link aria-label={FormatTimeDescription(transcriptionData.timestamp, false, true /* don't show hours, show seconds */)} styles={getAppTranscriptCardTimestampLinkStyles()} onClick={(event) => this._onTimestampClicked(event)} href="#">{FormatTime(transcriptionData.timestamp)}</Link>;

    return <Label aria-label={FormatTimeDescription(transcriptionData.timestamp, false, true /* don't show hours, show seconds */)} styles={getAppTranscriptCardTimestampLabelStyles()}>{FormatTime(transcriptionData.timestamp)}</Label>
  }

  private _onTimestampClicked = (event): void => {
    this.props.onTimestampClicked(this.props.transcriptionData.timestamp);
    if(event.detail == 1) { // event is because of mouse click
      (document.activeElement as HTMLElement).blur(); // remove focus from card
    }
  }

  private _onAddClicked = (event): void => {
    if (!this.props.addTranscriptionSegmentToDocument || !this.props.transcriptionData) {
      return;
    }
    this.props.addTranscriptionSegmentToDocument(this.props.transcriptionData.id);
    if(event.detail == 1) { // event is because of mouse click
      (document.activeElement as HTMLElement).blur(); // remove focus from card
    }
  }

  private _onEditClicked = (): void => {
    this.props.onEditing(this.props.transcriptionData.id);
  };

  private _onConfirmClicked = (event): void => {
    const { onTranscriptChange, transcriptionData } = this.props;

    if (event.detail == 0) { // event is because of keypress
      this.setState({
        isPendingEditButtonFocus: true
      });
    }

    if (onTranscriptChange && this.speakerTextField.current && this.transcriptionTextField.current && transcriptionData) {
      this.props.onTranscriptChange({
        id: transcriptionData.id,
        text: this.transcriptionTextField.current.value,
        speaker: this.speakerTextField.current.value,
        changeAllSpeakers: this.changeAllCheckboxRef.current.checked
      });
    }

    this.props.onEditingModeExit();
  };

  private _onCancelClicked = (event): void => {
    if (event.detail == 0) { // event is because of keypress
      this.setState({
        isPendingEditButtonFocus: true
      });
    }
    this.props.onEditingModeExit();
  };
}